import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { AppConfig } from 'src/app/app.config'
import { MeasurementUnits } from 'src/app/common/measurement-units.model'
import { ToastrService } from 'ngx-toastr'
import { filter, retryWhen, delay, takeWhile } from 'rxjs/operators'
import { SentryContextService } from '../../common/sentry-context.service'

interface ServerDeploymentSettings {
  id: number
  measurement_units: MeasurementUnits
}

@Injectable({
  providedIn: 'root',
})
export class CenterMeasurementUnitsService {
  constructor(
    private http: HttpClient, 
    private appConfig: AppConfig, 
    private toastr: ToastrService, 
    private sentryService: SentryContextService) {
  }

  private centerMeasurementUnitsSubject = new BehaviorSubject<MeasurementUnits | null>(null)
  centerMeasurementUnits$ = this.centerMeasurementUnitsSubject.asObservable().pipe(
    filter<MeasurementUnits>(Boolean),
  )

  loadMeasurmentUnits() {
    const url = this.appConfig.config.server_api.centers.deployment_settings

    this.http.get<ServerDeploymentSettings>(url).pipe(
      retryWhen(errors => errors.pipe(
        takeWhile((_err,  index) => assertRetriesNotExceeded(index)),
        delay(1000)),
      )
    ).subscribe(
      ({ measurement_units }) => this.centerMeasurementUnitsSubject.next(MeasurementUnits[measurement_units]),
      this.handleError.bind(this)
    )
  }


  setCenterMeasurementUnits = (measurementUnits: MeasurementUnits): Observable<object> => {
    this.centerMeasurementUnitsSubject.next(measurementUnits)
    const url = this.appConfig.config.server_api.centers.deployment_settings
    const measurement_units = Object.keys(MeasurementUnits).filter(
      (name) => MeasurementUnits[name] === measurementUnits,
    )?.[0]
    return this.http.post(url, { measurement_units })  
  }

  handleError(err: Error) {
    // Can't use ErrorHandlingService since it will create a circular dependency - 
    // ErrorHandlingService depends on unit conversion pipe which depends on CenterMeasurementUnitsService.
    // So reporting the error here to console, toastr, and sentry. Also propagating to centerMeasurementUnitsSubject.
    console.error(err, this)
    this.toastr.error(err.message)
    this.sentryService.reportError(err)
    
    this.centerMeasurementUnitsSubject.error(err)
  }
}


const RETRY_LIMIT = 3


function assertRetriesNotExceeded(retryNumber: number): boolean {
  if (retryNumber >= RETRY_LIMIT) {
    throw Error(`Failed to fetch the measurement unit set in the Center settings.
      Measurement units in the Live Missions screen will be displayed in metric units.`)
  }
  return true
}
